import React, { useState, useEffect } from 'react';
import _ from 'lodash';

// Types
type VideoFormat = 'tutorial' | 'vlog' | 'review' | 'shorts' | 'livestream' | 'interview';
type Platform = 'youtube' | 'tiktok' | 'instagram' | 'twitch';
type CreatorNiche = 'tech' | 'beauty' | 'gaming' | 'fitness' | 'education' | 'entertainment' | 'business' | 'travel';
type CreatorExperience = 'beginner' | 'intermediate' | 'advanced';
type EditingTool = 'capcut' | 'premiere' | 'finalcut' | 'davinci' | 'other';

interface VideoProject {
  id: string;
  title: string;
  description: string;
  format: VideoFormat;
  platform: Platform;
  script: string;
  checklist: ChecklistItem[];
  created: Date;
  lastModified: Date;
  status: 'draft' | 'in-progress' | 'complete';
  tags: string[];
  thumbnail?: string;
}

interface PlatformGuideline {
  platform: Platform;
  videoLength: string;
  aspectRatio: string;
  optimalUploadTime: string;
  recommendedFormats: VideoFormat[];
}

interface ContentIdea {
  id: string;
  title: string;
  description: string;
  format: VideoFormat;
  estimatedEngagement: 'low' | 'medium' | 'high';
  difficultyLevel: 'easy' | 'medium' | 'hard';
}

interface ChecklistItem {
  id: string;
  task: string;
  complete: boolean;
  stage: 'pre-production' | 'production' | 'post-production';
}

interface UserProfile {
  niche: CreatorNiche;
  platforms: Platform[];
  experience: CreatorExperience;
  keywords: string[];
}

// Mock data
const platformGuidelines: Record<Platform, PlatformGuideline> = {
    youtube: {
      platform: 'youtube',
      videoLength: '8-15 minutes',
      aspectRatio: '16:9',
      optimalUploadTime: 'Weekdays, 2-4 PM',
      recommendedFormats: ['tutorial', 'review', 'vlog', 'livestream', 'interview']
    },
    tiktok: {
      platform: 'tiktok',
      videoLength: '15-60 seconds',
      aspectRatio: '9:16',
      optimalUploadTime: 'Evenings, 7-9 PM',
      recommendedFormats: ['shorts', 'tutorial']
    },
    instagram: {
      platform: 'instagram',
      videoLength: '30-60 seconds for Reels, 10 minutes for IGTV',
      aspectRatio: '9:16 for Reels, 16:9 for IGTV',
      optimalUploadTime: 'Lunch hours, 11AM-1PM',
      recommendedFormats: ['shorts', 'vlog']
    },
    twitch: {
      platform: 'twitch',
      videoLength: '2+ hours',
      aspectRatio: '16:9',
      optimalUploadTime: 'Evenings, 6-10 PM',
      recommendedFormats: ['livestream', 'interview']
    }
  };
  
  // Content ideas generator function
  const generateContentIdeas = (profile: UserProfile): ContentIdea[] => {
    // This would be an AI-powered function in the real application
    const mockIdeas: ContentIdea[] = [
      {
        id: '1',
        title: `Top 5 ${_.capitalize(profile.niche)} Trends in 2025`,
        description: `Explore the most exciting ${profile.niche} trends that are shaping the industry this year.`,
        format: 'review',
        estimatedEngagement: 'high',
        difficultyLevel: 'medium'
      },
      {
        id: '2',
        title: `How I ${profile.experience === 'beginner' ? 'Started' : 'Mastered'} ${_.capitalize(profile.niche)} Content Creation`,
        description: 'Share your journey and provide valuable insights to aspiring creators.',
        format: 'vlog',
        estimatedEngagement: 'medium',
        difficultyLevel: 'easy'
      },
      {
        id: '3',
        title: `${_.capitalize(profile.niche)} Q&A: Answering Your Most Asked Questions`,
        description: 'Connect with your audience by addressing their most common questions.',
        format: 'livestream',
        estimatedEngagement: 'high',
        difficultyLevel: 'medium'
      },
      {
        id: '4',
        title: `Beginner's Guide to ${_.capitalize(profile.niche)}`,
        description: 'Break down complex concepts into easy-to-understand steps for newcomers.',
        format: 'tutorial',
        estimatedEngagement: 'high',
        difficultyLevel: 'medium'
      },
      {
        id: '5',
        title: `Day in the Life of a ${_.capitalize(profile.niche)} Creator`,
        description: 'Show behind-the-scenes of your creative process and daily routines.',
        format: 'shorts',
        estimatedEngagement: 'medium',
        difficultyLevel: 'easy'
      }
    ];
  
    return mockIdeas;
  };
  
  // Generate checklist based on video format
  const generateChecklist = (format: VideoFormat): ChecklistItem[] => {
    const baseChecklist: ChecklistItem[] = [
      {
        id: '1',
        task: 'Research topic and gather reference materials',
        complete: false,
        stage: 'pre-production'
      },
      {
        id: '2',
        task: 'Create content outline/script',
        complete: false,
        stage: 'pre-production'
      },
      {
        id: '3',
        task: 'Prepare equipment (camera, microphone, lighting)',
        complete: false,
        stage: 'pre-production'
      },
      {
        id: '4',
        task: 'Record video footage',
        complete: false,
        stage: 'production'
      },
      {
        id: '5',
        task: 'Record voiceover (if needed)',
        complete: false,
        stage: 'production'
      },
      {
        id: '6',
        task: 'Import and organize footage',
        complete: false,
        stage: 'post-production'
      },
      {
        id: '7',
        task: 'Edit video (cuts, transitions, effects)',
        complete: false,
        stage: 'post-production'
      },
      {
        id: '8',
        task: 'Add music and sound effects',
        complete: false,
        stage: 'post-production'
      },
      {
        id: '9',
        task: 'Create thumbnail and title',
        complete: false,
        stage: 'post-production'
      },
      {
        id: '10',
        task: 'Write description and add tags',
        complete: false,
        stage: 'post-production'
      },
      {
        id: '11',
        task: 'Review final video',
        complete: false,
        stage: 'post-production'
      },
      {
        id: '12',
        task: 'Export and upload',
        complete: false,
        stage: 'post-production'
      }
    ];
  
    // Additional format-specific tasks
    const formatSpecificTasks: Record<VideoFormat, ChecklistItem[]> = {
      tutorial: [
        {
          id: 'tutorial-1',
          task: 'Break down steps into teachable segments',
          complete: false,
          stage: 'pre-production'
        },
        {
          id: 'tutorial-2',
          task: 'Prepare visual aids or demonstrations',
          complete: false,
          stage: 'pre-production'
        },
        {
          id: 'tutorial-3',
          task: 'Add captions for key instructions',
          complete: false,
          stage: 'post-production'
        }
      ],
      vlog: [
        {
          id: 'vlog-1',
          task: 'Plan shooting locations',
          complete: false,
          stage: 'pre-production'
        },
        {
          id: 'vlog-2',
          task: 'Prepare talking points for narration',
          complete: false,
          stage: 'pre-production'
        }
      ],
      review: [
        {
          id: 'review-1',
          task: 'Thoroughly test product/service before filming',
          complete: false,
          stage: 'pre-production'
        },
        {
          id: 'review-2',
          task: 'Prepare pros/cons list',
          complete: false,
          stage: 'pre-production'
        },
        {
          id: 'review-3',
          task: 'Include product specifications and pricing',
          complete: false,
          stage: 'post-production'
        }
      ],
      shorts: [
        {
          id: 'shorts-1',
          task: 'Focus on one clear hook/concept',
          complete: false,
          stage: 'pre-production'
        },
        {
          id: 'shorts-2',
          task: 'Plan for vertical 9:16 format',
          complete: false,
          stage: 'pre-production'
        },
        {
          id: 'shorts-3',
          task: 'Add text overlays for key points',
          complete: false,
          stage: 'post-production'
        }
      ],
      livestream: [
        {
          id: 'livestream-1',
          task: 'Schedule and promote stream in advance',
          complete: false,
          stage: 'pre-production'
        },
        {
          id: 'livestream-2',
          task: 'Test internet connection and streaming setup',
          complete: false,
          stage: 'pre-production'
        },
        {
          id: 'livestream-3',
          task: 'Prepare topics/activities to fill stream time',
          complete: false,
          stage: 'pre-production'
        }
      ],
      interview: [
        {
          id: 'interview-1',
          task: 'Prepare question list',
          complete: false,
          stage: 'pre-production'
        },
        {
          id: 'interview-2',
          task: 'Schedule and brief interviewee',
          complete: false,
          stage: 'pre-production'
        },
        {
          id: 'interview-3',
          task: 'Test dual microphone setup',
          complete: false,
          stage: 'pre-production'
        }
      ]
    };
  
    return [...baseChecklist, ...formatSpecificTasks[format]];
  };

  // Main Application Component
const CreatorsSuiteApp = () => {
    // State
    const [step, setStep] = useState<'questionnaire' | 'recommendations' | 'ideas' | 'checklist' | 'guide' | 'projects' | 'editor'>('questionnaire');
    const [userProfile, setUserProfile] = useState<UserProfile>({
      niche: 'tech',
      platforms: ['youtube'],
      experience: 'intermediate',
      keywords: []
    });
    const [selectedFormat, setSelectedFormat] = useState<VideoFormat | null>(null);
    const [selectedPlatform, setSelectedPlatform] = useState<Platform | null>(null);
    const [contentIdeas, setContentIdeas] = useState<ContentIdea[]>([]);
    const [selectedIdea, setSelectedIdea] = useState<ContentIdea | null>(null);
    const [checklist, setChecklist] = useState<ChecklistItem[]>([]);
    const [keyword, setKeyword] = useState('');
    const [projects, setProjects] = useState<VideoProject[]>([]);
    const [currentProject, setCurrentProject] = useState<VideoProject | null>(null);
    const [editingTool, setEditingTool] = useState<EditingTool>('capcut');
    const [scriptContent, setScriptContent] = useState('');
    const [promptInput, setPromptInput] = useState('');
    const [chatHistory, setChatHistory] = useState<{role: 'user' | 'assistant', content: string}[]>([]);
    const [showSidebar, setShowSidebar] = useState(true);
  
    // Load saved projects from localStorage
    useEffect(() => {
      const savedProjects = localStorage.getItem('creatorSuiteProjects');
      if (savedProjects) {
        try {
          const parsedProjects = JSON.parse(savedProjects);
          setProjects(parsedProjects.map((p: any) => ({
            ...p,
            created: new Date(p.created),
            lastModified: new Date(p.lastModified)
          })));
        } catch (error) {
          console.error('Error loading saved projects', error);
        }
      }
    }, []);
  
    // Save projects to localStorage when updated
    useEffect(() => {
      if (projects.length > 0) {
        localStorage.setItem('creatorSuiteProjects', JSON.stringify(projects));
      }
    }, [projects]);
  
    // Handle questionnaire submission
    const handleQuestionnaireSubmit = () => {
      setStep('recommendations');
    };
  
    // Handle format selection
    const handleSelectFormat = (format: VideoFormat) => {
      setSelectedFormat(format);
      setStep('ideas');
      setContentIdeas(generateContentIdeas(userProfile));
    };
  
    // Handle platform selection
    const handleSelectPlatform = (platform: Platform) => {
      setSelectedPlatform(platform);
    };
  
    // Handle idea selection
    const handleSelectIdea = (idea: ContentIdea) => {
      setSelectedIdea(idea);
      setChecklist(generateChecklist(idea.format));
      setStep('checklist');
    };
  
    // Handle checklist item toggle
    const handleToggleChecklistItem = (id: string) => {
      setChecklist(prev => 
        prev.map(item => 
          item.id === id ? { ...item, complete: !item.complete } : item
        )
      );
    };
  
    // Handle adding keyword
    const handleAddKeyword = () => {
      if (keyword && !userProfile.keywords.includes(keyword)) {
        setUserProfile(prev => ({
          ...prev,
          keywords: [...prev.keywords, keyword]
        }));
        setKeyword('');
      }
    };
  
    // Go to detailed guide
    const handleGoToGuide = () => {
      setStep('guide');
    };
  
    // Progress calculation for checklist
    const calculateProgress = (stage: 'pre-production' | 'production' | 'post-production') => {
      const stageItems = checklist.filter(item => item.stage === stage);
      const completedItems = stageItems.filter(item => item.complete);
      return stageItems.length > 0 ? (completedItems.length / stageItems.length) * 100 : 0;
    };
  
    // Export checklist as PDF (mock function)
    const handleExport = () => {
      alert('Exporting checklist as PDF... (This would be implemented with a PDF library)');
    };
  
    // Share via email (mock function)
    const handleShare = () => {
      alert('Sharing via email... (This would open an email dialog)');
    };
    // Create a new project from the selected idea
  const handleCreateProject = () => {
    if (!selectedIdea || !selectedPlatform || !selectedFormat) return;
    
    const newProject: VideoProject = {
      id: Date.now().toString(),
      title: selectedIdea.title,
      description: selectedIdea.description,
      format: selectedFormat,
      platform: selectedPlatform,
      script: generateInitialScript(selectedIdea, selectedFormat),
      checklist: checklist,
      created: new Date(),
      lastModified: new Date(),
      status: 'draft',
      tags: userProfile.keywords
    };
    
    setProjects(prev => [...prev, newProject]);
    setCurrentProject(newProject);
    setScriptContent(newProject.script);
    setStep('editor');
    
    // Add initial assistant message
    setChatHistory([
      {
        role: 'assistant',
        content: `I've created a new ${selectedFormat} project for ${selectedPlatform}. You can edit the script, update the checklist, or ask me for specific instructions on how to create this video.`
      }
    ]);
  };
  
  // Generate initial script based on idea and format
  const generateInitialScript = (idea: ContentIdea, format: VideoFormat): string => {
    switch (format) {
      case 'tutorial':
        return `# ${idea.title}\n\n## Introduction\n- Welcome viewers\n- Briefly explain what they'll learn\n- Mention why this is important/valuable\n\n## Main Content\n1. Step 1: [Describe first step]\n2. Step 2: [Describe second step]\n3. Step 3: [Describe third step]\n\n## Conclusion\n- Recap what was covered\n- Suggest next steps\n- Call to action\n\n## Notes\n- Key points to emphasize\n- B-roll suggestions\n- On-screen text ideas`;
      
      case 'vlog':
        return `# ${idea.title}\n\n## Intro Shot\n- Location: [where to film]\n- Action: [what you're doing]\n- Opening line: "Hey everyone, welcome back to my channel!"\n\n## Main Segments\n1. [First location/activity]\n   - What to show\n   - What to talk about\n2. [Second location/activity]\n   - Key moments to capture\n   - Story to tell\n\n## Closing Thoughts\n- Reflection on the experience\n- Teaser for next video\n- Outro and call to action`;
      
      case 'review':
        return `# ${idea.title}\n\n## Introduction\n- What are you reviewing?\n- Why did you choose it?\n- Brief overview of what you'll cover\n\n## Product Overview\n- Specs and features\n- Price point\n- First impressions\n\n## Testing Experience\n- How you tested it\n- What worked well\n- Pain points or issues\n\n## Verdict\n- Final rating\n- Who this is good for\n- Alternatives to consider\n\n## Closing\n- Summary recommendation\n- Call to action`;
      
      default:
        return `# ${idea.title}\n\n## Introduction\n- Hook: Grab viewer attention\n- Introduce topic\n- What they'll gain from watching\n\n## Main Content\n- Key point 1\n- Key point 2\n- Key point 3\n\n## Conclusion\n- Recap\n- Call to action\n- End screen`;
    }
  };
  
  // Handle prompt submission
  const handlePromptSubmit = () => {
    if (!promptInput.trim()) return;
    
    // Add user message to chat
    setChatHistory(prev => [...prev, { role: 'user', content: promptInput }]);
    
    // Process the prompt and generate response
    const response = processPrompt(promptInput);
    setChatHistory(prev => [...prev, { role: 'assistant', content: response }]);
    
    setPromptInput('');
  };
  
  // Process user prompts and return appropriate responses
  const processPrompt = (prompt: string): string => {
    const lowerPrompt = prompt.toLowerCase();
    
    // Check for script editing requests
    if (lowerPrompt.includes('edit script') || lowerPrompt.includes('change script') || lowerPrompt.includes('update script')) {
      return "I've opened the script editor for you. You can directly edit the content in the editor panel.";
    }
    
    // Check for title editing
    if (lowerPrompt.includes('change title') || lowerPrompt.includes('edit title') || lowerPrompt.includes('new title')) {
      const titleMatch = prompt.match(/title to ["'](.+?)["']/i);
      if (titleMatch && currentProject) {
        const newTitle = titleMatch[1];
        setCurrentProject({...currentProject, title: newTitle, lastModified: new Date()});
        setProjects(prev => prev.map(p => p.id === currentProject.id ? {...p, title: newTitle, lastModified: new Date()} : p));
        return `I've updated the title to "${newTitle}".`;
      }
      return "What would you like the new title to be?";
    }
    
    // Check for CapCut instructions
    if (lowerPrompt.includes('capcut') || lowerPrompt.includes('how to edit')) {
      if (currentProject?.format === 'tutorial') {
        return `## CapCut Instructions for Tutorial Videos\n\n1. **Open CapCut**:\n   - Launch CapCut on your device\n   - Click "New Project"\n   - Select 16:9 aspect ratio for YouTube\n\n2. **Import Media**:\n   - Click "Import" and select your tutorial footage\n   - Organize clips in folders (intro, steps, conclusion)\n\n3. **Create Structure**:\n   - Start with intro clip (5-10 seconds)\n   - Add title text: Click Text > Title > Choose template\n   - For each step in your tutorial:\n     * Add text overlay with step number\n     * Use "Split" to cut between explanations\n\n4. **Add Effects**:\n   - Use "Speed" to speed up repetitive actions\n   - Add zoom effects for emphasis on important details\n   - Consider "Green Screen" for picture-in-picture explanations\n\n5. **Export Settings**:\n   - Resolution: 1080p or 4K\n   - Frame rate: 30fps\n   - Format: MP4\n\nWould you like more specific instructions for any of these steps?`;
      } else {
        return `## Basic CapCut Workflow\n\n1. **Open CapCut**:\n   - Launch the application\n   - Start a new project (Select appropriate aspect ratio)\n\n2. **Import Your Media**:\n   - Click "Import" and select your video clips and images\n   - Drag files to the timeline in your desired order\n\n3. **Basic Editing**:\n   - Trim clips: Drag the edges of clips in the timeline\n   - Split clips: Position playhead and click "Split"\n   - Adjust speed: Select clip > Speed > Adjust slider\n\n4. **Add Text and Effects**:\n   - Text: Click "Text" > Choose style > Type your content\n   - Effects: Select clip > Effects > Choose effect\n   - Transitions: Click between clips > Transitions\n\n5. **Add Audio**:\n   - Background music: Audio > Music > Select track\n   - Adjust volume levels for balance\n\n6. **Export Your Video**:\n   - Click "Export" button\n   - Choose quality settings (recommend 1080p, 30fps)\n   - Save to your device\n\nWould you like more detailed instructions for your specific ${currentProject?.format} video?`;
      }
    }
    
    // Check for specific platform advice
    if (currentProject && (lowerPrompt.includes('youtube tips') || lowerPrompt.includes('tiktok tips') || lowerPrompt.includes('instagram tips'))) {
      if (currentProject.platform === 'youtube') {
        return `## YouTube Optimization Tips\n\n1. **Title Optimization**:\n   - Keep titles under 60 characters\n   - Front-load keywords\n   - Use numbers and emotional triggers\n   - Example: "5 Proven Ways to Boost Your Productivity (Works Instantly)"\n\n2. **Thumbnail Creation**:\n   - Use bright, contrasting colors\n   - Include close-up of face showing emotion\n   - Add minimal text (3-5 words max)\n   - Make it readable on mobile\n\n3. **Description Optimization**:\n   - First 2-3 lines are most important (visible before "Show more")\n   - Include main keywords in the first paragraph\n   - Add timestamps for videos longer than 5 minutes\n   - Include links to products/resources mentioned\n\n4. **Tags Strategy**:\n   - Start with exact match of your title\n   - Include variations of your main keyword\n   - Add broader category tags\n   - Research competitor tags using VidIQ or TubeBuddy\n\n5. **Engagement Tactics**:\n   - Ask a question in the first 30 seconds\n   - Include a call-to-action midway\n   - End with clear subscription request\n   - Respond to early comments`;
      } else if (currentProject.platform === 'tiktok') {
        return `## TikTok Success Strategies\n\n1. **Hook in 3 Seconds**:\n   - Start with a bold statement or question\n   - Show the end result first\n   - Use text overlay to clarify what they'll see\n\n2. **Trending Audio**:\n   - Browse the Discover page for trending sounds\n   - Use viral music but create unique visuals\n   - Repurpose sounds with your niche twist\n\n3. **Video Structure**:\n   - Keep total length under 30 seconds (15-20s ideal)\n   - Main point or surprise should come at 7-10 second mark\n   - End with call-to-action or question\n\n4. **Caption Strategy**:\n   - Begin with hook or curiosity gap\n   - Use 3-5 relevant hashtags (mix of broad and niche)\n   - Ask a question to encourage comments\n\n5. **Posting Schedule**:\n   - Post 1-3 times daily\n   - Best times: 6-9am, 11am-1pm, 7-9pm\n   - Consistency matters more than perfect timing`;
      } else if (currentProject.platform === 'instagram') {
        return `## Instagram Reels Tips\n\n1. **Format Requirements**:\n   - Optimal ratio: 9:16 vertical\n   - Maximum length: 90 seconds\n   - Keep important elements centered\n\n2. **Hook Strategy**:\n   - Pattern interrupts (surprising visual or statement)\n   - "Wait for it..." teaser\n   - In-your-face text that creates curiosity\n\n3. **Content Structure**:\n   - First 3 seconds must grab attention\n   - Main content: Simple, fast-paced with text overlays\n   - End with clear CTA (call to action)\n\n4. **Caption Optimization**:\n   - Front-load value in first line before "more"\n   - Use line breaks for readability\n   - 8-15 relevant hashtags (mix trending and niche)\n   - Include a question to boost comments\n\n5. **Audio Selection**:\n   - Use trending sounds from Instagram's library\n   - Add voiceover for tutorials\n   - Consider original audio for recognition`;
      }
    }
    
    // Default response for other queries
    return `I can help you with specific instructions for creating your ${currentProject?.format} video. Would you like help with:\n\n- Editing the script or title\n- Step-by-step instructions for recording\n- How to edit this in CapCut or another editor\n- Platform-specific optimization tips\n- Checklist management\n\nJust let me know what you need!`;
  };

  return (
    <div className="min-h-screen bg-gray-900 p-4">
      <header className="flex justify-between items-center mb-8">
        <div className="flex items-center">
          <button 
            className="mr-4 text-blue-400 hover:text-blue-300 p-2"
            onClick={() => setShowSidebar(!showSidebar)}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
          <div>
            <h1 className="text-3xl font-bold text-blue-400">Creator's Suite</h1>
            <p className="text-gray-300">Your all-in-one video content creation assistant</p>
          </div>
        </div>
        <div className="flex space-x-4">
          <button 
            className={`px-4 py-2 rounded-md transition-colors ${step === 'projects' ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
            onClick={() => setStep('projects')}
          >
            My Projects
          </button>
          <button 
            className={`px-4 py-2 rounded-md transition-colors ${step === 'questionnaire' ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'}`}
            onClick={() => setStep('questionnaire')}
          >
            New Project
          </button>
        </div>
      </header>

      <div className="flex">
        {showSidebar && step !== 'questionnaire' && step !== 'recommendations' && step !== 'ideas' && (
          <div className="w-64 bg-gray-800 rounded-lg shadow-md p-4 mr-4">
            <h3 className="text-lg font-medium text-blue-300 mb-4">My Projects</h3>
            <div className="space-y-2">
              {projects.map(project => (
                <div 
                  key={project.id}
                  className={`p-2 rounded-md cursor-pointer ${currentProject?.id === project.id ? 'bg-blue-500 text-white' : 'hover:bg-gray-700 text-gray-300'}`}
                  onClick={() => {
                    setCurrentProject(project);
                    setScriptContent(project.script);
                    setChecklist(project.checklist);
                    setSelectedFormat(project.format);
                    setSelectedPlatform(project.platform);
                    setStep('editor');
                  }}
                >
                  <p className="font-medium truncate">{project.title}</p>
                  <div className="flex justify-between text-xs">
                    <span>{project.format}</span>
                    <span>{new Date(project.lastModified).toLocaleDateString()}</span>
                  </div>
                </div>
              ))}
              {projects.length === 0 && (
                <div className="text-gray-500 text-center py-4">
                  No projects yet
                </div>
              )}
            </div>
          </div>
        )}
        
        <main className={`${showSidebar && step !== 'questionnaire' && step !== 'recommendations' && step !== 'ideas' ? 'flex-1' : 'w-full max-w-4xl mx-auto'} bg-gray-800 rounded-lg shadow-md p-6 text-gray-100`}>
          {/* Step indicators */}
          {(step === 'questionnaire' || step === 'recommendations' || step === 'ideas' || step === 'checklist' || step === 'guide') && (
            <div className="flex justify-between mb-8">
              {['questionnaire', 'recommendations', 'ideas', 'checklist', 'guide'].map((s, index) => (
                <div 
                  key={s} 
                  className={`flex flex-col items-center ${index > ['questionnaire', 'recommendations', 'ideas', 'checklist', 'guide'].indexOf(step) ? 'text-gray-500' : 'text-blue-300'}`}
                >
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center mb-2 ${index > ['questionnaire', 'recommendations', 'ideas', 'checklist', 'guide'].indexOf(step) ? 'bg-gray-700' : 'bg-blue-500 text-white'}`}>
                    {index + 1}
                  </div>
                  <span className="text-xs capitalize">{s}</span>
                </div>
              ))}
            </div>
          )}
          {/* Questionnaire */}
          {step === 'questionnaire' && (
            <div className="space-y-6">
              <h2 className="text-xl font-semibold text-blue-300 mb-4">Tell us about your content</h2>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-gray-300 mb-2">What's your content niche?</label>
                  <select 
                    className="w-full p-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-blue-400 focus:outline-none"
                    value={userProfile.niche}
                    onChange={e => setUserProfile(prev => ({ ...prev, niche: e.target.value as CreatorNiche }))}
                  >
                    <option value="tech">Technology</option>
                    <option value="beauty">Beauty & Fashion</option>
                    <option value="gaming">Gaming</option>
                    <option value="fitness">Fitness & Health</option>
                    <option value="education">Education</option>
                    <option value="entertainment">Entertainment</option>
                    <option value="business">Business</option>
                    <option value="travel">Travel</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-gray-300 mb-2">Which platforms do you create for?</label>
                  <div className="flex flex-wrap gap-4">
                    {['youtube', 'tiktok', 'instagram', 'twitch'].map((platform) => (
                      <label key={platform} className="inline-flex items-center bg-gray-700 p-2 rounded-md border border-gray-600 hover:border-blue-400 cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={userProfile.platforms.includes(platform as Platform)}
                          onChange={() => {
                            setUserProfile(prev => {
                              const platforms = prev.platforms.includes(platform as Platform)
                                ? prev.platforms.filter(p => p !== platform)
                                : [...prev.platforms, platform as Platform];
                              return { ...prev, platforms };
                            });
                          }}
                          className="mr-2"
                        />
                        <span className="capitalize">{platform}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-gray-300 mb-2">What's your experience level?</label>
                  <div className="flex gap-4">
                    {['beginner', 'intermediate', 'advanced'].map((level) => (
                      <label 
                        key={level} 
                        className={`inline-flex items-center p-2 rounded-md cursor-pointer transition-colors ${
                          userProfile.experience === level 
                            ? 'bg-blue-500 text-white' 
                            : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                        }`}
                      >
                        <input
                          type="radio"
                          checked={userProfile.experience === level}
                          onChange={() => {
                            setUserProfile(prev => ({ ...prev, experience: level as CreatorExperience }));
                          }}
                          className="mr-2"
                        />
                        <span className="capitalize">{level}</span>
                      </label>
                    ))}
                  </div>
                </div>
                
                <div>
                  <label className="block text-gray-300 mb-2">Add keywords relevant to your content:</label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={keyword}
                      onChange={e => setKeyword(e.target.value)}
                      className="flex-1 p-2 bg-gray-700 border border-gray-600 rounded-md text-white focus:border-blue-400 focus:outline-none"
                      placeholder="Enter a keyword"
                    />
                    <button 
                      onClick={handleAddKeyword}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors"
                    >
                      Add
                    </button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {userProfile.keywords.map((kw, index) => (
                      <span key={index} className="bg-gray-700 border border-gray-600 px-3 py-1 rounded-md text-sm flex items-center">
                        {kw}
                        <button 
                          onClick={() => {
                            setUserProfile(prev => ({
                              ...prev,
                              keywords: prev.keywords.filter((_, i) => i !== index)
                            }));
                          }}
                          className="ml-2 text-gray-400 hover:text-white"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
              
              <button
                onClick={handleQuestionnaireSubmit}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-md mt-6 font-medium transition-colors w-full md:w-auto"
              >
                Get Recommendations
              </button>
            </div>
          )}

          {/* Recommendations */}
          {step === 'recommendations' && (
            <div className="space-y-8">
              <h2 className="text-xl font-semibold text-blue-300 mb-4">Recommended for You</h2>
              
              <div>
                <h3 className="font-medium mb-4 text-gray-300">Select a platform to continue:</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  {userProfile.platforms.map((platform) => (
                    <div 
                      key={platform}
                      onClick={() => handleSelectPlatform(platform)}
                      className={`border p-4 rounded-lg cursor-pointer flex flex-col items-center transition-all ${
                        selectedPlatform === platform 
                          ? 'border-blue-400 bg-gray-700 shadow-lg shadow-blue-400/20' 
                          : 'border-gray-600 bg-gray-800 hover:border-gray-400'
                      }`}
                    >
                      <div className={`w-14 h-14 flex items-center justify-center rounded-full mb-3 ${
                        selectedPlatform === platform ? 'bg-blue-500 text-white' : 'bg-gray-700 text-gray-300'
                      }`}>
                        {platform === 'youtube' && 'YT'}
                        {platform === 'tiktok' && 'TT'}
                        {platform === 'instagram' && 'IG'}
                        {platform === 'twitch' && 'TW'}
                      </div>
                      <span className="text-center capitalize">{platform}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {selectedPlatform && (
                <>
                  <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 mt-6">
                    <h3 className="font-medium mb-4 text-blue-300">Platform Guidelines: {_.capitalize(selectedPlatform)}</h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="bg-gray-800 p-4 rounded-lg">
                        <p className="text-sm text-gray-400 mb-2">Optimal Video Length:</p>
                        <p className="font-medium text-white">{platformGuidelines[selectedPlatform].videoLength}</p>
                      </div>
                      <div className="bg-gray-800 p-4 rounded-lg">
                        <p className="text-sm text-gray-400 mb-2">Aspect Ratio:</p>
                        <p className="font-medium text-white">{platformGuidelines[selectedPlatform].aspectRatio}</p>
                      </div>
                      <div className="bg-gray-800 p-4 rounded-lg">
                        <p className="text-sm text-gray-400 mb-2">Best Time to Upload:</p>
                        <p className="font-medium text-white">{platformGuidelines[selectedPlatform].optimalUploadTime}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-8">
                    <h3 className="font-medium mb-4 text-blue-300">Recommended Video Formats</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
                      {platformGuidelines[selectedPlatform].recommendedFormats.map((format) => (
                        <div 
                          key={format}
                          onClick={() => handleSelectFormat(format)}
                          className="bg-gray-800 border border-gray-600 p-4 rounded-lg cursor-pointer hover:border-blue-400 transition-colors"
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-medium capitalize text-white">{format}</h4>
                            <div className="w-2 h-2 rounded-full bg-blue-400"></div>
                          </div>
                          <p className="text-sm text-gray-400">
                            {format === 'tutorial' && 'Educational step-by-step content'}
                            {format === 'vlog' && 'Personal, day-in-the-life content'}
                            {format === 'review' && 'Product or experience evaluations'}
                            {format === 'shorts' && 'Quick, engaging vertical videos'}
                            {format === 'livestream' && 'Real-time interactive broadcasts'}
                            {format === 'interview' && 'Conversations with guests'}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Content Ideas */}
          {step === 'ideas' && selectedFormat && (
            <div className="space-y-8">
              <h2 className="text-xl font-semibold text-blue-300">Content Ideas for {_.capitalize(selectedFormat)}</h2>
              
              <div className="space-y-4">
                {contentIdeas.map((idea) => (
                  <div 
                    key={idea.id}
                    className="border border-gray-600 bg-gray-800 p-5 rounded-lg cursor-pointer hover:border-blue-400 transition-colors"
                    onClick={() => handleSelectIdea(idea)}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1 pr-4">
                        <h3 className="font-medium text-white text-lg">{idea.title}</h3>
                        <p className="text-gray-400 mt-2">{idea.description}</p>
                      </div>
                      <div className="flex flex-col items-end">
                        <span className={`text-xs px-3 py-1 rounded-full font-medium ${
                          idea.estimatedEngagement === 'high' 
                            ? 'bg-green-900 text-green-300' 
                            : idea.estimatedEngagement === 'medium'
                              ? 'bg-blue-900 text-blue-300'
                              : 'bg-gray-700 text-gray-300'
                        }`}>
                          {_.capitalize(idea.estimatedEngagement)} engagement
                        </span>
                        <span className="text-xs text-gray-400 mt-2 bg-gray-700 px-3 py-1 rounded-full">
                          {_.capitalize(idea.difficultyLevel)} difficulty
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="bg-gray-700 p-6 rounded-lg border border-gray-600 mt-6">
                <h3 className="font-medium mb-4 text-blue-300">Need more ideas?</h3>
                <div className="flex gap-2">
                  <input
                    type="text"
                    className="flex-1 p-3 bg-gray-800 border border-gray-600 rounded-md text-white focus:border-blue-400 focus:outline-none"
                    placeholder="Enter a specific topic..."
                  />
                  <button className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-md transition-colors font-medium">
                    Generate More
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Checklist */}
          {step === 'checklist' && selectedIdea && (
            <div className="space-y-8">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-semibold text-blue-300">Production Checklist</h2>
                <div className="space-x-2">
                  <button 
                    onClick={handleExport}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-md text-sm transition-colors"
                  >
                    Export PDF
                  </button>
                  <button 
                    onClick={handleShare}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-md text-sm transition-colors"
                  >
                    Share
                  </button>
                </div>
              </div>
              
              <div className="bg-blue-900/30 p-6 rounded-lg border border-blue-800/50">
                <h3 className="font-medium text-white text-lg">{selectedIdea.title}</h3>
                <p className="text-gray-300 mt-2">{selectedIdea.description}</p>
              </div>

              {['pre-production', 'production', 'post-production'].map((stage) => (
                <div key={stage} className="bg-gray-800 border border-gray-700 rounded-lg p-6 mt-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="font-medium text-blue-300 capitalize">{stage.replace('-', ' ')}</h3>
                    <div className="flex items-center">
                      <div className="w-36 bg-gray-700 rounded-full h-3 mr-2">
                        <div 
                          className="bg-blue-500 h-3 rounded-full" 
                          style={{ width: `${calculateProgress(stage as any)}%` }}
                        ></div>
                      </div>
                      <span className="text-xs text-gray-400">
                        {Math.round(calculateProgress(stage as any))}%
                      </span>
                    </div>
                  </div>
                  
                  <div className="space-y-3">
                    {checklist
                      .filter(item => item.stage === stage)
                      .map((item) => (
                        <div 
                          key={item.id}
                          className={`flex items-center p-4 border rounded-md transition-colors ${
                            item.complete 
                              ? 'border-blue-800 bg-blue-900/20' 
                              : 'border-gray-600 bg-gray-700 hover:border-gray-500'
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={item.complete}
                            onChange={() => handleToggleChecklistItem(item.id)}
                            className="mr-3 h-5 w-5 accent-blue-500"
                          />
                          <span className={item.complete ? 'line-through text-gray-400' : 'text-white'}>
                            {item.task}
                          </span>
                        </div>
                      ))}
                  </div>
                </div>
              ))}
              
              <div className="flex gap-4 mt-6">
                <button
                  onClick={handleGoToGuide}
                  className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-md w-full font-medium transition-colors flex items-center justify-center"
                >
                  <span>View Detailed Guide</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L12.586 11H5a1 1 0 110-2h7.586l-2.293-2.293a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                
                <button
                  onClick={handleCreateProject}
                  className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-md w-full font-medium transition-colors flex items-center justify-center"
                >
                  <span>Create Project</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 ml-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>
          )}
          
          {/* Detailed Guide */}
          {step === 'guide' && selectedIdea && (
            <div className="space-y-8">
              <div className="flex items-center mb-6">
                <button 
                  onClick={() => setStep('checklist')}
                  className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-md mr-4 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                <h2 className="text-xl font-semibold text-blue-300">Step-by-Step Guide: {_.capitalize(selectedIdea.format)}</h2>
              </div>
              
              <div className="bg-gray-800 p-8 rounded-lg border border-gray-700 space-y-8">
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold mr-4">1</div>
                  <div className="flex-1 space-y-3">
                    <h3 className="font-medium text-white text-lg">Planning & Research</h3>
                    <ul className="list-disc pl-5 space-y-2 text-gray-300">
                      <li>Identify your main goal for this {selectedIdea.format}</li>
                      <li>Research trending topics in your niche</li>
                      <li>Analyze successful videos in this format</li>
                      <li>Create a content outline with key points</li>
                    </ul>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold mr-4">2</div>
                  <div className="flex-1 space-y-3">
                    <h3 className="font-medium text-white text-lg">Scripting & Storyboarding</h3>
                    <ul className="list-disc pl-5 space-y-2 text-gray-300">
                      <li>Write a compelling hook/intro (first 15 seconds)</li>
                      <li>Outline main content sections</li>
                      <li>Plan visual transitions between sections</li>
                      <li>Create a clear call-to-action</li>
                    </ul>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold mr-4">3</div>
                  <div className="flex-1 space-y-3">
                    <h3 className="font-medium text-white text-lg">Equipment Setup</h3>
                    <ul className="list-disc pl-5 space-y-2 text-gray-300">
                      <li>Camera positioned at eye level</li>
                      <li>Check lighting (3-point setup if possible)</li>
                      <li>Test audio levels and reduce background noise</li>
                      <li>Prepare any props or visual aids</li>
                    </ul>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold mr-4">4</div>
                  <div className="flex-1 space-y-3">
                    <h3 className="font-medium text-white text-lg">Filming Tips</h3>
                    <ul className="list-disc pl-5 space-y-2 text-gray-300">
                      <li>Record in segments for easier editing</li>
                      <li>Film extra B-roll footage for cutaways</li>
                      <li>Check framing and composition</li>
                      <li>Do multiple takes of important sections</li>
                    </ul>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold mr-4">5</div>
                  <div className="flex-1 space-y-3">
                    <h3 className="font-medium text-white text-lg">Editing Workflow</h3>
                    <ul className="list-disc pl-5 space-y-2 text-gray-300">
                      <li>Create a rough cut with primary footage</li>
                      <li>Add B-roll and cutaways to enhance visual interest</li>
                      <li>Apply color correction and audio enhancement</li>
                      <li>Add text overlays and graphics as needed</li>
                      <li>Incorporate music that matches your content's tone</li>
                    </ul>
                  </div>
                </div>
                
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-bold mr-4">6</div>
                  <div className="flex-1 space-y-3">
                    <h3 className="font-medium text-white text-lg">Publishing Strategy</h3>
                    <ul className="list-disc pl-5 space-y-2 text-gray-300">
                      <li>Optimize title with keywords (but avoid clickbait)</li>
                      <li>Create an eye-catching thumbnail</li>
                      <li>Write detailed description with timestamps</li>
                      <li>Add relevant tags for discoverability</li>
                      <li>Schedule for optimal posting time: {selectedPlatform && platformGuidelines[selectedPlatform].optimalUploadTime}</li>
                    </ul>
                  </div>
                </div>
                
                <div className="bg-blue-900/30 p-6 rounded-lg border border-blue-800/50 mt-8">
                  <h3 className="font-medium text-blue-300 text-lg mb-4">Platform-Specific Tips for {selectedPlatform && _.capitalize(selectedPlatform)}</h3>
                  <ul className="list-disc pl-5 space-y-3 text-gray-300">
                    {selectedPlatform === 'youtube' && (
                      <>
                        <li>Add end screens and cards to promote other videos</li>
                        <li>Consider adding chapters for longer content</li>
                        <li>Optimize for search with keyword-rich descriptions</li>
                      </>
                    )}
                    {selectedPlatform === 'tiktok' && (
                      <>
                        <li>Get to the point immediately (first 3 seconds)</li>
                        <li>Use trending sounds and effects</li>
                        <li>Participate in challenges for more visibility</li>
                      </>
                    )}
                    {selectedPlatform === 'instagram' && (
                      <>
                        <li>Create a visually appealing first frame</li>
                        <li>Use maximum hashtags (30) for discovery</li>
                        <li>Consider cross-posting to Stories and Feed</li>
                      </>
                    )}
                    {selectedPlatform === 'twitch' && (
                      <>
                        <li>Interact with chat frequently</li>
                        <li>Set up channel points and rewards</li>
                        <li>Create a consistent streaming schedule</li>
                      </>
                    )}
                  </ul>
                </div>
              </div>
              
              <div className="flex gap-4 justify-center mt-8">
                <button
                  onClick={() => setStep('checklist')}
                  className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-md transition-colors"
                >
                  Back to Checklist
                </button>
                <button
                  className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-md transition-colors font-medium"
                  onClick={handleCreateProject}
                >
                  Create Project
                </button>
              </div>
            </div>
          )}
          
          {/* Projects List View */}
          {step === 'projects' && (
            <div className="space-y-8">
              <h2 className="text-xl font-semibold text-blue-300 mb-6">My Video Projects</h2>
              
              {projects.length === 0 ? (
                <div className="bg-gray-700 p-8 rounded-lg text-center">
                  <p className="text-gray-400 mb-4">You don't have any projects yet.</p>
                  <button
                    onClick={() => setStep('questionnaire')}
                    className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-md font-medium transition-colors"
                  >
                    Create Your First Project
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {projects.map(project => (
                    <div 
                      key={project.id} 
                      className="bg-gray-700 rounded-lg overflow-hidden border border-gray-600 hover:border-blue-400 transition-colors cursor-pointer"
                      onClick={() => {
                        setCurrentProject(project);
                        setScriptContent(project.script);
                        setChecklist(project.checklist);
                        setSelectedFormat(project.format);
                        setSelectedPlatform(project.platform);
                        setStep('editor');
                      }}
                    >
                      <div className="p-5">
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="text-lg font-medium text-white">{project.title}</h3>
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            project.status === 'draft' ? 'bg-gray-600 text-gray-300' :
                            project.status === 'in-progress' ? 'bg-blue-900 text-blue-300' :
                            'bg-green-900 text-green-300'
                          }`}>
                            {_.startCase(project.status)}
                          </span>
                        </div>
                        <p className="text-gray-400 text-sm mb-4 line-clamp-2">{project.description}</p>
                        <div className="flex justify-between text-sm">
                          <div className="flex space-x-2">
                            <span className="bg-gray-800 px-2 py-1 rounded text-blue-300 capitalize">{project.platform}</span>
                            <span className="bg-gray-800 px-2 py-1 rounded text-green-300 capitalize">{project.format}</span>
                          </div>
                          <span className="text-gray-400">
                            {new Date(project.lastModified).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <div className="bg-gray-800 px-5 py-3 flex justify-between">
                        <div className="flex items-center">
                          <div className="w-4 h-4 bg-gray-600 rounded-full mr-2"></div>
                          <span className="text-xs text-gray-400">
                            {project.checklist.filter(item => item.complete).length} / {project.checklist.length} tasks
                          </span>
                        </div>
                        <button 
                          className="text-xs text-blue-300 hover:text-blue-200"
                          onClick={(e) => {
                            e.stopPropagation();
                            const updatedProjects = projects.filter(p => p.id !== project.id);
                            setProjects(updatedProjects);
                            if (currentProject?.id === project.id) {
                              setCurrentProject(null);
                            }
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              
              <button
                onClick={() => setStep('questionnaire')}
                className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-md font-medium transition-colors flex items-center mx-auto mt-8"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                </svg>
                Create New Project
              </button>
            </div>
          )}
          
          {/* Project Editor View */}
          {step === 'editor' && currentProject && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <button 
                    className="mr-4 bg-gray-700 hover:bg-gray-600 text-white p-2 rounded-md transition-colors"
                    onClick={() => setStep('projects')}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                    </svg>
                  </button>
                  <div>
                    <h2 className="text-xl font-semibold text-blue-300">{currentProject.title}</h2>
                    <div className="flex space-x-2 text-xs mt-1">
                      <span className="bg-gray-700 px-2 py-1 rounded capitalize">{currentProject.platform}</span>
                      <span className="bg-gray-700 px-2 py-1 rounded capitalize">{currentProject.format}</span>
                      <span className={`px-2 py-1 rounded ${
                        currentProject.status === 'draft' ? 'bg-gray-600 text-gray-300' :
                        currentProject.status === 'in-progress' ? 'bg-blue-900 text-blue-300' :
                        'bg-green-900 text-green-300'
                      }`}>
                        {_.startCase(currentProject.status)}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="space-x-2">
                  <select 
                    className="bg-gray-700 border border-gray-600 rounded-md p-2 text-sm text-white"
                    value={currentProject.status}
                    onChange={(e) => {
                      const newStatus = e.target.value as 'draft' | 'in-progress' | 'complete';
                      setCurrentProject({...currentProject, status: newStatus, lastModified: new Date()});
                      setProjects(prev => prev.map(p => 
                        p.id === currentProject.id ? 
                          {...p, status: newStatus, lastModified: new Date()} : 
                          p
                      ));
                    }}
                  >
                    <option value="draft">Draft</option>
                    <option value="in-progress">In Progress</option>
                    <option value="complete">Complete</option>
                  </select>
                  
                  <button
                    onClick={handleShare}
                    className="bg-gray-700 hover:bg-gray-600 text-white px-3 py-2 rounded-md text-sm transition-colors"
                  >
                    Share
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Script Editor */}
                <div className="bg-gray-700 rounded-lg border border-gray-600 overflow-hidden">
                  <div className="bg-gray-800 px-4 py-3 flex justify-between items-center">
                    <h3 className="font-medium text-white">Script</h3>
                    <div className="space-x-2">
                      <select 
                        className="bg-gray-700 border border-gray-600 rounded-md p-1 text-sm text-white"
                        value={editingTool}
                        onChange={(e) => setEditingTool(e.target.value as EditingTool)}
                      >
                        <option value="capcut">CapCut</option>
                        <option value="premiere">Premiere Pro</option>
                        <option value="finalcut">Final Cut Pro</option>
                        <option value="davinci">DaVinci Resolve</option>
                        <option value="other">Other</option>
                      </select>
                      <button
                        className="text-blue-300 hover:text-blue-200 text-sm"
                        onClick={() => {
                          // Save script to project
                          setCurrentProject({...currentProject, script: scriptContent, lastModified: new Date()});
                          setProjects(prev => prev.map(p => 
                            p.id === currentProject.id ? 
                              {...p, script: scriptContent, lastModified: new Date()} : 
                              p
                          ));
                        }}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                  <textarea
                    value={scriptContent}
                    onChange={(e) => setScriptContent(e.target.value)}
                    className="w-full h-64 bg-gray-800 p-4 text-white border-none focus:outline-none focus:ring-1 focus:ring-blue-400 resize-none font-mono text-sm"
                  />
                </div>
                
                {/* AI Assistant */}
                <div className="bg-gray-700 rounded-lg border border-gray-600 overflow-hidden flex flex-col">
                  <div className="bg-gray-800 px-4 py-3">
                    <h3 className="font-medium text-white">AI Assistant</h3>
                  </div>
                  <div className="flex-1 overflow-y-auto p-4 space-y-4" style={{maxHeight: '280px'}}>
                    {chatHistory.map((msg, index) => (
                      <div 
                        key={index} 
                        className={`flex ${msg.role === 'assistant' ? 'justify-start' : 'justify-end'}`}
                      >
                        <div 
                          className={`max-w-3/4 p-3 rounded-lg ${
                            msg.role === 'assistant' 
                              ? 'bg-gray-800 text-white' 
                              : 'bg-blue-600 text-white'
                          }`}
                        >
                          {msg.content.includes('##') ? (
                            <div>
                              {msg.content.split('\n').map((line, i) => (
                                <div key={i}>
                                  {line.startsWith('##') ? (
                                    <h4 className="font-bold text-blue-300 text-base mb-1">{line.replace('##', '')}</h4>
                                  ) : line.startsWith('#') ? (
                                    <h3 className="font-bold text-lg mb-2">{line.replace('#', '')}</h3>
                                  ) : line.match(/^\d+\.\s/) ? (
                                    <p className="mb-2 font-medium">{line}</p>
                                  ) : line.startsWith('-') ? (
                                    <p className="ml-2 mb-1">{line}</p>
                                  ) : line.startsWith('   -') ? (
                                    <p className="ml-6 mb-1">{line.replace('   -', '-')}</p>
                                  ) : (
                                    <p className="mb-2">{line}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p>{msg.content}</p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 bg-gray-800 flex">
                    <input
                      type="text"
                      value={promptInput}
                      onChange={(e) => setPromptInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handlePromptSubmit()}
                      placeholder="Ask for specific instructions..."
                      className="flex-1 bg-gray-700 border border-gray-600 rounded-l-md p-2 text-white focus:outline-none focus:border-blue-400"
                    />
                    <button
                      onClick={handlePromptSubmit}
                      className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-r-md transition-colors"
                    >
                      Send
                    </button>
                  </div>
                </div>
              </div>
              
              {/* Detailed Instructions Panel */}
              <div className="mt-6">
                <div className="bg-gray-700 rounded-lg border border-gray-600 overflow-hidden">
                  <div className="bg-gray-800 px-4 py-3 flex justify-between items-center">
                    <h3 className="font-medium text-white">Video Production Steps</h3>
                    <span className="text-xs text-gray-400">
                      {currentProject.checklist.filter(item => item.complete).length} / {currentProject.checklist.length} completed
                    </span>
                  </div>
                  
                  <div className="p-4">
                    <div className="mb-6 bg-blue-900/30 p-4 rounded-lg border border-blue-800/50">
                      <h4 className="font-medium text-blue-300 mb-2">Step-by-Step Instructions for {_.capitalize(currentProject.format)} on {_.capitalize(currentProject.platform)}</h4>
                      
                      {currentProject.format === 'tutorial' && (
                        <div className="space-y-4">
                          <div className="border-l-2 border-blue-500 pl-3">
                            <h5 className="text-white font-medium">1. Pre-Production</h5>
                            <ul className="text-gray-300 text-sm space-y-1 mt-1">
                              <li>• Research your topic thoroughly (include statistics and facts)</li>
                              <li>• Create a step-by-step outline with clear, manageable chunks</li>
                              <li>• Prepare visual aids, examples, and demonstrations</li>
                              <li>• Organize your workspace and tools before filming</li>
                            </ul>
                          </div>
                          
                          <div className="border-l-2 border-blue-500 pl-3">
                            <h5 className="text-white font-medium">2. Recording Setup</h5>
                            <ul className="text-gray-300 text-sm space-y-1 mt-1">
                              <li>• Position camera at eye level or slightly above</li>
                              <li>• Set up 3-point lighting (key, fill, and back lights)</li>
                              <li>• Use lapel/lavalier mic for clear audio</li>
                              <li>• Prepare screen recording software if needed</li>
                              <li>• Record short test clip to check audio and framing</li>
                            </ul>
                          </div>
                          
                          <div className="border-l-2 border-blue-500 pl-3">
                            <h5 className="text-white font-medium">3. Filming Tips</h5>
                            <ul className="text-gray-300 text-sm space-y-1 mt-1">
                              <li>• Start with a hook mentioning viewer benefit</li>
                              <li>• Introduce yourself and establish credibility</li>
                              <li>• Explain what you'll cover (show final result first)</li>
                              <li>• Film each step separately for easier editing</li>
                              <li>• Use clear, deliberate movements</li>
                              <li>• Speak slightly slower than normal conversation</li>
                            </ul>
                          </div>
                          
                          <div className="border-l-2 border-blue-500 pl-3">
                            <h5 className="text-white font-medium">4. CapCut Editing Instructions</h5>
                            <ul className="text-gray-300 text-sm space-y-1 mt-1">
                              <li>• <span className="text-blue-300">Open CapCut</span> and start a new project</li>
                              <li>• <span className="text-blue-300">Import your footage</span> and organize clips by steps</li>
                              <li>• <span className="text-blue-300">Add intro sequence</span> (5-10 seconds)</li>
                              <li>• <span className="text-blue-300">Create text overlays</span> for each step number</li>
                              <li>• <span className="text-blue-300">Use speed adjustments</span> for repetitive actions (2-4x)</li>
                              <li>• <span className="text-blue-300">Add zoom effects</span> to highlight important details</li>
                              <li>• <span className="text-blue-300">Include on-screen callouts</span> for key points</li>
                              <li>• <span className="text-blue-300">Add background music</span> at 10-15% volume</li>
                              <li>• <span className="text-blue-300">Export in 1080p</span> (or 4K if available)</li>
                            </ul>
                          </div>
                        </div>
                      )}
                      
                      {currentProject.format === 'vlog' && (
                        <div className="space-y-4">
                          <div className="border-l-2 border-blue-500 pl-3">
                            <h5 className="text-white font-medium">1. Planning Your Vlog</h5>
                            <ul className="text-gray-300 text-sm space-y-1 mt-1">
                              <li>• Plan 3-5 interesting locations or activities</li>
                              <li>• Create a loose storyline with beginning, middle, and end</li>
                              <li>• Pack essential gear: camera, microphone, extra batteries</li>
                              <li>• Consider lighting conditions at different times of day</li>
                            </ul>
                          </div>
                          
                          <div className="border-l-2 border-blue-500 pl-3">
                            <h5 className="text-white font-medium">2. Filming Techniques</h5>
                            <ul className="text-gray-300 text-sm space-y-1 mt-1">
                              <li>• Begin with an engaging opening shot (scenic view or action)</li>
                              <li>• Mix talking head shots with B-roll footage</li>
                              <li>• Use movement: walking shots, pans, and tilts</li>
                              <li>• Capture natural moments and reactions</li>
                              <li>• Film transitions between locations</li>
                            </ul>
                          </div>
                          
                          <div className="border-l-2 border-blue-500 pl-3">
                            <h5 className="text-white font-medium">3. CapCut Editing Workflow</h5>
                            <ul className="text-gray-300 text-sm space-y-1 mt-1">
                              <li>• <span className="text-blue-300">Open CapCut</span> and start a new project</li>
                              <li>• <span className="text-blue-300">Import and organize footage</span> by location/activity</li>
                              <li>• <span className="text-blue-300">Create a storyboard sequence</span> first (rough cut)</li>
                              <li>• <span className="text-blue-300">Add transitions</span> between scenes (0.5-1 second)</li>
                              <li>• <span className="text-blue-300">Enhance colors</span> using filters or manual adjustment</li>
                              <li>• <span className="text-blue-300">Add text overlays</span> for locations or timestamps</li>
                              <li>• <span className="text-blue-300">Include upbeat background music</span> (adjust levels during talking)</li>
                              <li>• <span className="text-blue-300">Add sound effects</span> for emphasis (subtle)</li>
                              <li>• <span className="text-blue-300">Export in appropriate format</span> based on platform</li>
                            </ul>
                          </div>
                        </div>
                      )}
                      
                      {(currentProject.format !== 'tutorial' && currentProject.format !== 'vlog') && (
                        <div className="space-y-4">
                          <div className="border-l-2 border-green-500 pl-3 mb-4">
                            <p className="text-gray-300 text-sm">Ask the AI Assistant for specific instructions for your {currentProject.format} video by typing: "How do I create a {currentProject.format} for {currentProject.platform}?"</p>
                          </div>
                          
                          <div className="border-l-2 border-blue-500 pl-3">
                            <h5 className="text-white font-medium">1. Pre-Production</h5>
                            <ul className="text-gray-300 text-sm space-y-1 mt-1">
                              <li>• Research your topic thoroughly</li>
                              <li>• Create a content outline with key points</li>
                              <li>• Prepare any equipment or props needed</li>
                            </ul>
                          </div>
                          
                          <div className="border-l-2 border-blue-500 pl-3">
                            <h5 className="text-white font-medium">2. Basic CapCut Workflow</h5>
                            <ul className="text-gray-300 text-sm space-y-1 mt-1">
                              <li>• <span className="text-blue-300">Launch CapCut</span> and start a new project</li>
                              <li>• <span className="text-blue-300">Import your media</span> (video clips and images)</li>
                              <li>• <span className="text-blue-300">Arrange clips</span> in the timeline</li>
                              <li>• <span className="text-blue-300">Add text overlays</span> for key information</li>
                              <li>• <span className="text-blue-300">Add background music</span> and adjust audio levels</li>
                              <li>• <span className="text-blue-300">Apply transitions</span> between clips</li>
                              <li>• <span className="text-blue-300">Export</span> in appropriate quality for your platform</li>
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Checklist */}
                    <div className="mt-4">
                      <h4 className="font-medium text-white mb-3">Production Checklist</h4>
                      <div className="space-y-4">
                        {['pre-production', 'production', 'post-production'].map((stage) => (
                          <div key={stage}>
                            <h5 className="text-blue-300 text-sm font-medium capitalize mb-2">{stage.replace('-', ' ')}</h5>
                            <div className="space-y-2">
                              {currentProject.checklist
                                .filter(item => item.stage === stage)
                                .map((item) => (
                                  <div 
                                    key={item.id}
                                    className={`flex items-center p-3 rounded-md ${
                                      item.complete 
                                        ? 'bg-blue-900/20 border border-blue-800/40' 
                                        : 'bg-gray-800 border border-gray-700'
                                    }`}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={item.complete}
                                      onChange={() => {
                                        const updatedChecklist = currentProject.checklist.map(i => 
                                          i.id === item.id ? {...i, complete: !i.complete} : i
                                        );
                                        setCurrentProject({
                                          ...currentProject,
                                          checklist: updatedChecklist,
                                          lastModified: new Date()
                                        });
                                        setProjects(prev => prev.map(p => 
                                          p.id === currentProject.id ? 
                                            {...p, checklist: updatedChecklist, lastModified: new Date()} : 
                                            p
                                        ));
                                      }}
                                      className="mr-3 h-5 w-5 accent-blue-500"
                                    />
                                    <span className={item.complete ? 'line-through text-gray-400' : 'text-white'}>
                                      {item.task}
                                    </span>
                                  </div>
                                ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>
      </div>
      
      <footer className="mt-12 text-center text-gray-500 text-sm p-4">
        <p>Creator's Suite © 2025 | Professional tools for content creators</p>
      </footer>
    </div>
  );
};

export default CreatorsSuiteApp;